import { useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { FileCheck2, Upload, X, ExternalLink } from "lucide-react";

/**
 * CoaDropzone — admin drag-and-drop for a product's Certificate(s) of Analysis.
 *
 * Multi-COA aware: dropzone stays ACTIVE after upload so admins can attach a
 * separate COA per variant/lot. Each uploaded COA is listed below with an
 * inline delete button. Backend endpoints:
 *   POST   /admin/products/{id}/coa          — append (multipart file)
 *   DELETE /admin/products/{id}/coa/{coaId}  — remove one
 *   GET    /products/{id}/coa/{coaId}        — buyer stream
 *   GET    /products/{id}/coa                — legacy: first COA
 *
 * Accepts PDF/PNG/JPEG up to 8 MB per file.
 */
export default function CoaDropzone({ product, onChange }) {
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileInput = useRef(null);
  const coas = product.coas || [];
  const previewUrlFor = (coaId) =>
    `${process.env.REACT_APP_BACKEND_URL}/api/products/${product.id}/coa/${coaId}`;

  const upload = async (file) => {
    if (!file) return;
    if (!["application/pdf", "image/png", "image/jpeg"].includes(file.type)) {
      toast.error("COA must be a PDF, PNG, or JPEG");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("COA is too large (max 8 MB)");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/products/${product.id}/coa`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`COA uploaded (${(data.coas || []).length} on file)`);
      onChange?.(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const removeOne = async (coa) => {
    if (!window.confirm(`Remove "${coa.filename}" from this product?`)) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/admin/products/${product.id}/coa/${coa.id}`);
      toast.success("COA removed");
      onChange?.(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Remove failed");
    } finally {
      setBusy(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    // Support multi-file drop — upload each sequentially so the API sees one
    // at a time and the array grows deterministically.
    const files = Array.from(e.dataTransfer.files || []);
    (async () => {
      for (const f of files) {
        // eslint-disable-next-line no-await-in-loop
        await upload(f);
      }
    })();
  };

  const onFileInput = (e) => {
    const files = Array.from(e.target.files || []);
    (async () => {
      for (const f of files) {
        // eslint-disable-next-line no-await-in-loop
        await upload(f);
      }
      if (fileInput.current) fileInput.current.value = "";
    })();
  };

  return (
    <div className="mt-3">
      {coas.length > 0 && (
        <ul className="mb-2 space-y-1.5" data-testid={`coa-list-${product.slug}`}>
          {coas.map((c) => (
            <li
              key={c.id}
              className="flex items-center gap-2 border border-emerald-300 bg-emerald-50 rounded-lg px-3 py-2 text-xs"
              data-testid={`coa-item-${product.slug}-${c.id}`}
            >
              <FileCheck2 size={14} className="text-emerald-700 shrink-0" />
              <a
                href={previewUrlFor(c.id)}
                target="_blank"
                rel="noreferrer"
                className="flex-1 truncate text-emerald-900 hover:text-emerald-950 underline decoration-emerald-500/40"
                title={c.filename}
                data-testid={`coa-preview-${product.slug}-${c.id}`}
              >
                {c.filename || "certificate.pdf"}
              </a>
              <ExternalLink size={11} className="text-emerald-700/60 shrink-0" />
              <button
                type="button"
                onClick={() => removeOne(c)}
                disabled={busy}
                className="text-emerald-800/70 hover:text-oxblood transition-colors ml-1 disabled:opacity-40"
                title="Remove this COA"
                data-testid={`coa-remove-${product.slug}-${c.id}`}
              >
                <X size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => !busy && fileInput.current?.click()}
        className={`border border-dashed rounded-lg px-3 py-3 text-xs text-center cursor-pointer transition-colors ${
          dragOver
            ? "border-oxblood bg-oxblood/5 text-oxblood"
            : "border-borderSubtle bg-bone/30 text-stone hover:border-charcoal hover:text-charcoal"
        } ${busy ? "opacity-60 cursor-wait" : ""}`}
        data-testid={`coa-dropzone-${product.slug}`}
      >
        <Upload size={14} className="inline mr-1.5 -mt-0.5" />
        {busy
          ? "Uploading…"
          : dragOver
            ? "Drop to upload"
            : coas.length > 0
              ? "Drop another COA here or click"
              : "Drop COA here or click"}
        <input
          ref={fileInput}
          type="file"
          accept="application/pdf,image/png,image/jpeg"
          multiple
          onChange={onFileInput}
          className="hidden"
          data-testid={`coa-file-input-${product.slug}`}
        />
      </div>
    </div>
  );
}
